import { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Clapperboard, Copy, Download, Check, Mic, ListChecks, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { TUTORIAL_MODULES, buildTutorialMarkdown, moduleToText } from '../data/tutorialScripts';

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
    return true;
  } catch {
    toast.error('Copy failed — select and copy manually');
    return false;
  }
};

const ModuleCard = ({ m }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null); // 'script' | 'bullets' | 'all'

  const flash = (key) => { setCopied(key); setTimeout(() => setCopied(null), 1500); };

  return (
    <Card className="overflow-hidden" data-testid={`tutorial-module-${m.id}`}>
      <CardHeader className="cursor-pointer select-none" onClick={() => setOpen((v) => !v)} data-testid={`tutorial-toggle-${m.id}`}>
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0">{m.number}</span>
          <CardTitle className="text-base flex-1">{m.title}</CardTitle>
          <Badge variant="outline" className="text-[11px] text-zinc-500">{m.runtime}</Badge>
          {m.adminOnly && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">admin-only</Badge>}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => copyText(m.voiceover, 'Voiceover script').then((ok) => ok && flash('script'))}
              data-testid={`copy-script-${m.id}`}
            >
              {copied === 'script' ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Mic className="w-3.5 h-3.5 mr-1.5" />}
              Copy voiceover
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => copyText(m.slides.map((s) => `${s.title}\n${s.bullets.map((b) => `• ${b}`).join('\n')}`).join('\n\n'), 'Slide bullets').then((ok) => ok && flash('bullets'))}
              data-testid={`copy-bullets-${m.id}`}
            >
              {copied === 'bullets' ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <ListChecks className="w-3.5 h-3.5 mr-1.5" />}
              Copy slide bullets
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => copyText(moduleToText(m), 'Full module').then((ok) => ok && flash('all'))}
              data-testid={`copy-all-${m.id}`}
            >
              {copied === 'all' ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              Copy both
            </Button>
          </div>

          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              <Mic className="w-3.5 h-3.5" /> Voiceover script (HeyGen-ready)
            </p>
            <pre className="whitespace-pre-wrap text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md p-3 font-sans leading-relaxed" data-testid={`script-text-${m.id}`}>{m.voiceover}</pre>
          </div>

          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              <ListChecks className="w-3.5 h-3.5" /> On-screen slides / bullets
            </p>
            <div className="space-y-3">
              {m.slides.map((s, i) => (
                <div key={i} className="bg-white border border-zinc-200 rounded-md p-3">
                  <p className="font-semibold text-sm text-zinc-900 mb-1.5">{s.title}</p>
                  <ul className="space-y-1">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="text-sm text-zinc-600 flex gap-2">
                        <span className="text-indigo-400">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const AdminTutorialScriptsPage = () => {
  const downloadMarkdown = () => {
    const md = buildTutorialMarkdown();
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'PostToSocial_Tutorial_Scripts.md';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Downloaded PostToSocial_Tutorial_Scripts.md');
  };

  const copyEntire = () => copyText(buildTutorialMarkdown(), 'Entire series');

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-tutorial-scripts-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
              <Clapperboard className="w-6 h-6 text-indigo-600" />
              Tutorial Scripts
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              AI-avatar voiceover scripts + on-screen slide bullets for the {TUTORIAL_MODULES.length}-video tutorial series.
              Copy a module or download the whole set, then paste into HeyGen.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyEntire} data-testid="copy-entire-series-btn">
              <Copy className="w-4 h-4 mr-2" /> Copy all
            </Button>
            <Button onClick={downloadMarkdown} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="download-scripts-btn">
              <Download className="w-4 h-4 mr-2" /> Download .md
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {TUTORIAL_MODULES.map((m) => <ModuleCard key={m.id} m={m} />)}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminTutorialScriptsPage;
